import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Bell, Calendar, ChevronDown, Link as LinkIcon, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import styles from './AddRecordModal.module.css';
import StudentRegular from '../../shared/assets/categories/StudentRegular.png';
import ServingFood from '../../shared/assets/categories/ServingFood.png';
import HouseLineRegular from '../../shared/assets/categories/HouseLineRegular.png';
import SecurityValidation from '../../shared/assets/categories/SecurityValidation.png';
import AddMoneyCircle from '../../shared/assets/categories/AddMoneyCircle.png';
import EmojiFunnySquare from '../../shared/assets/categories/EmojiFunnySquare.png';
import Healtcare from '../../shared/assets/categories/Healtcare.png';
import Shopping from '../../shared/assets/categories/ShoppingBag.png';
import Taxes from '../../shared/assets/categories/Taxes.png';
import ConvertRange from '../../shared/assets/categories/ScisTransparentSupply.png';
import SelfTransfer from '../../shared/assets/categories/SelfTransfer.png';
import HomeBolt from '../../shared/assets/categories/HomeBolt.png';
import Healthcare from '../../shared/assets/categories/Healthcare.png';
import Gift from '../../shared/assets/categories/Gift.png';
import DatabaseDollar from '../../shared/assets/categories/DatabaseDollar.png';
import ReportMoney from '../../shared/assets/categories/ReportMoney.png';
import DeviceDesktopDollar from '../../shared/assets/categories/DeviceDesktopDollar.png';
import HomeDollar from '../../shared/assets/categories/HomeDollar.png';
import MoneybagPlus from '../../shared/assets/categories/MoneybagPlus.png';
import UserDollar from '../../shared/assets/categories/UserDollar.png';
import IncomeBusiness from '../../shared/assets/income/income_business.png';
import IncomeClient from '../../shared/assets/income/income_client.png';
import IncomeEmployer from '../../shared/assets/income/income_employer.png';
import IncomeInvestment from '../../shared/assets/income/income_investment.png';
import IncomeOther from '../../shared/assets/income/income_other.png';
import IncomeRental from '../../shared/assets/income/income_rental.png';

import { QrCode } from '../../shared/assets/icons';

import { useAuth } from '../../context/AuthContext';
import { sortIncomeCategories } from '../../shared/lib/category/sort-income-categories';
import {
	expensesCreateRequest,
	expensesGerCategoriesRequest,
	expensesUpdateRequest,
	recurringExpensesCreateRequest,
	recurringExpensesUpdateRequest,
} from '../../modules/Expenses/api/index';
import {
	incomesCreateRequest,
	incomesGerCategoriesRequest,
	incomesGerSourcesRequest,
	incomesUpdateRequest,
	recurringIncomesCreateRequest,
	recurringIncomesUpdateRequest,
} from '../../modules/Income/api/index';
import { acceptRecurringNotificationSkipCreate } from '../../entities/notification/api';
import { SUPPORTED_CURRENCY_CODES } from '../../constants/currencies';

const LAST_USED_RECORD_DATE_KEY = 'addRecordLastDate';

/** Parse date-only string (YYYY-MM-DD) as local date - no UTC/timezone conversion. */
function parseDateOnly(dateStr) {
	if (!dateStr) return null;
	const part = String(dateStr).split(' ')[0];
	const parts = part.split('-').map(Number);
	if (parts.length !== 3) return null;
	const [year, month, day] = parts;
	if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
	return new Date(year, month - 1, day);
}

function getLastUsedRecordDate() {
	try {
		const stored = sessionStorage.getItem(LAST_USED_RECORD_DATE_KEY);
		return parseDateOnly(stored) ?? new Date();
	} catch {
		return new Date();
	}
}

function saveLastUsedRecordDate(dateValue) {
	if (!dateValue || Number.isNaN(dateValue.getTime())) return;
	try {
		sessionStorage.setItem(
			LAST_USED_RECORD_DATE_KEY,
			format(dateValue, 'yyyy-MM-dd'),
		);
	} catch {
		// Ignore quota / private-mode failures; date just falls back to today.
	}
}

// Custom Input component for DatePicker
const CustomDateInput = React.forwardRef(
	({ value, onClick, onChange, error }, ref) => (
		<div
			className={`${styles.selectField} ${
				error ? styles.selectFieldError : ''
			}`}
			onClick={onClick}
			ref={ref}
		>
			<Calendar size={18} className={styles.fieldIcon} />
			<span className={value ? styles.selectedValue : styles.placeholder}>
				{value || 'Select date'}
			</span>
			<ChevronDown size={18} className={styles.chevronIcon} />
		</div>
	),
);

CustomDateInput.displayName = 'CustomDateInput';

const AddRecordModal = ({
	open,
	onClose,
	onSuccess,
	defaultTab = 'income',
	editingExpense = null,
	editingIncome = null,
	prefillFromRecurringNotification = null,
	/** Opens a new record with the recurring switch already on (e.g. the Calendar,
      where a one-off record would be saved and then not show up anywhere). */
	defaultRecurring = false,
	/** Date a new record starts on - YYYY-MM-DD or a Date. Defaults to the last
      one used, as before. */
	defaultDate = null,
	/** When set, skip expense/income APIs and call this after validation (e.g. statement draft edit). */
	onCustomSave = null,
	isDuplicating = false,
}) => {
	const intl = useIntl();
	const { userInfo } = useAuth();
	const userLanguage = userInfo?.language_preference || 'en';
	const [activeTab, setActiveTab] = useState(defaultTab);
	const [incomeCategories, setIncomeCategories] = useState([]);
	const [expenseCategories, setExpenseCategories] = useState([]);
	const [incomeSources, setIncomeSources] = useState([]);
	const [amount, setAmount] = useState('');
	const [currency, setCurrency] = useState('AMD');
	const [category, setCategory] = useState({ id: '', name: '' });
	const [source, setSource] = useState({ id: '', name: '' });
	const [date, setDate] = useState(getLastUsedRecordDate);
	const [description, setDescription] = useState('');
	const [frequency, setFrequency] = useState(
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	);
	const [remindMe, setRemindMe] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showError, setShowError] = useState('');
	const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
	const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
	const [showSourceDropdown, setShowSourceDropdown] = useState(false);
	const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
	const [content, setContent] = useState('');
	const [fieldErrors, setFieldErrors] = useState({
		amount: false,
		category: false,
		source: false,
		date: false,
	});

	// State for backend specific field errors
	const [backendFieldErrors, setBackendFieldErrors] = useState({
		amount: false,
		category: false,
		source: false,
		date: false,
	});
	const [descriptionLength, setDescriptionLength] = useState(0);
	const MAX_DESCRIPTION_LENGTH = 255;
	// Refs for dropdowns
	const currencyRef = useRef(null);
	const categoryRef = useRef(null);
	const sourceRef = useRef(null);
	const frequencyRef = useRef(null);
	const editableRef = useRef(null);
	const urlRegex = /(https?:\/\/[^\s]+)/g;
	const frequencies = [
		intl.formatMessage({ id: 'AddRecordModalID25' }),
		intl.formatMessage({ id: 'AddRecordModalID24' }),
	];

	// Map translated frequency label → backend value
	const frequencyToRecurringPeriod = freq => {
		const weeklyMsg = intl.formatMessage({ id: 'AddRecordModalID25' });
		const monthlyMsg = intl.formatMessage({ id: 'AddRecordModalID24' });
		if (freq === weeklyMsg) return 'weekly';
		if (freq === monthlyMsg) return 'monthly';
		return 'monthly'; // default fallback
	};

	const categoryData = [
		{ name: 'All', icon: null },
		// { name: "Business", icon: CashCoin },
		{ name: 'Education', icon: StudentRegular },
		{ name: 'Food & Dining', icon: ServingFood },
		{ name: 'Housing', icon: HouseLineRegular },
		{ name: 'Insurance', icon: SecurityValidation },
		{ name: 'Other', icon: AddMoneyCircle },
		{ name: 'Entertainment', icon: EmojiFunnySquare },
		{ name: 'Personal Care', icon: Healtcare },
		{ name: 'Shopping', icon: Shopping },
		{ name: 'Transportation', icon: ConvertRange },
		{ name: 'Travel', icon: SelfTransfer },
		{ name: 'Utilities', icon: HomeBolt },
		{ name: 'Healthcare', icon: Healthcare },
		{ name: 'Taxes', icon: Taxes },
		{ name: 'Gifts & Donations', icon: Gift },
		{ name: 'Business', icon: DatabaseDollar },
		{ name: 'Investment', icon: ReportMoney },
		{ name: 'Freelance', icon: DeviceDesktopDollar },
		{ name: 'Rental', icon: HomeDollar },
		{ name: 'Salary', icon: MoneybagPlus },
		{ name: 'Other', icon: UserDollar },
	];

	const convertTextToHTML = text => {
		return text.replace(urlRegex, url => {
			return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">${url}</a>`;
		});
	};

	const saveCursorPosition = () => {
		const selection = window.getSelection();
		if (!selection.rangeCount) return null;

		const range = selection.getRangeAt(0);
		const preCaretRange = range.cloneRange();
		preCaretRange.selectNodeContents(editableRef.current);
		preCaretRange.setEnd(range.endContainer, range.endOffset);
		const caretOffset = preCaretRange.toString().length;

		return caretOffset;
	};

	const restoreCursorPosition = offset => {
		if (offset === null || !editableRef.current) return;

		const selection = window.getSelection();
		const range = document.createRange();

		let currentOffset = 0;
		let found = false;

		const walk = node => {
			if (found) return;

			if (node.nodeType === Node.TEXT_NODE) {
				const textLength = node.textContent.length;
				if (currentOffset + textLength >= offset) {
					range.setStart(node, offset - currentOffset);
					range.collapse(true);
					found = true;
					return;
				}
				currentOffset += textLength;
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				for (let i = 0; i < node.childNodes.length; i++) {
					walk(node.childNodes[i]);
					if (found) return;
				}
			}
		};

		walk(editableRef.current);

		if (found) {
			selection.removeAllRanges();
			selection.addRange(range);
		}
	};

	const handleInput = e => {
		const text = e.target.innerText || '';
		const trimmedText = text.trim();
		const previousContent = content;

		// Only update if content actually changed
		if (trimmedText === previousContent) return;

		// Update both content and description - use empty string if no text
		const newContent = trimmedText;
		setContent(newContent);
		setDescription(newContent); // Sync description with content
		setDescriptionLength(newContent.length);
		// Save cursor position
		const cursorPosition = saveCursorPosition();

		// Convert URLs to links (only if there's text)
		const htmlWithLinks = newContent ? convertTextToHTML(newContent) : '';

		// Only update HTML if it changed
		if (e.target.innerHTML !== htmlWithLinks) {
			e.target.innerHTML = htmlWithLinks;

			// Restore cursor position
			restoreCursorPosition(cursorPosition);
		}
	};
	const handlePaste = e => {
		e.preventDefault();
		const text = e.clipboardData.getData('text/plain');
		document.execCommand('insertText', false, text);
	};

	const handleFocus = () => {
		// Ensure cursor is at the start when field is empty
		if (editableRef.current && !description && !content) {
			setTimeout(() => {
				const range = document.createRange();
				const selection = window.getSelection();
				range.selectNodeContents(editableRef.current);
				range.collapse(true); // Collapse to start
				selection.removeAllRanges();
				selection.addRange(range);
			}, 0);
		}
	};

	// Get current categories based on active tab
	const getCurrentCategories = () => {
		if (activeTab === 'income') {
			return incomeCategories;
		} else if (activeTab === 'expense') {
			return expenseCategories;
		} else if (activeTab === 'goal') {
			return [];
		}
		return [];
	};

	const currentCategories = getCurrentCategories();

	// Auto-clear error after 5 seconds
	useEffect(() => {
		if (showError) {
			const timer = setTimeout(() => {
				setShowError('');
			}, 5000);

			return () => clearTimeout(timer);
		}
	}, [showError]);

	// Clear field errors when user starts typing
	useEffect(() => {
		if (amount) {
			const amountValue = parseFloat(amount);
			// Only clear error if amount is valid (greater than 0)
			if (!isNaN(amountValue) && amountValue > 0) {
				setFieldErrors(prev => ({ ...prev, amount: false }));
				setBackendFieldErrors(prev => ({ ...prev, amount: false }));
			}
		}
	}, [amount]);

	useEffect(() => {
		if (category.id) {
			setFieldErrors(prev => ({ ...prev, category: false }));
			setBackendFieldErrors(prev => ({ ...prev, category: false }));
		}
	}, [category.id]);

	useEffect(() => {
		if (source.id) {
			setFieldErrors(prev => ({ ...prev, source: false }));
			setBackendFieldErrors(prev => ({ ...prev, source: false }));
		}
	}, [source.id]);

	useEffect(() => {
		if (date) {
			setFieldErrors(prev => ({ ...prev, date: false }));
			setBackendFieldErrors(prev => ({ ...prev, date: false }));
		}
	}, [date]);

	// Handle clicks outside dropdowns
	useEffect(() => {
		const handleClickOutside = event => {
			if (currencyRef.current && !currencyRef.current.contains(event.target)) {
				setShowCurrencyDropdown(false);
			}
			if (categoryRef.current && !categoryRef.current.contains(event.target)) {
				setShowCategoryDropdown(false);
			}
			if (sourceRef.current && !sourceRef.current.contains(event.target)) {
				setShowSourceDropdown(false);
			}
			if (
				frequencyRef.current &&
				!frequencyRef.current.contains(event.target)
			) {
				setShowFrequencyDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				// Fetch all data in parallel
				const [expenseRes, incomeRes, sourcesRes] = await Promise.all([
					expensesGerCategoriesRequest(),
					incomesGerCategoriesRequest(),
					incomesGerSourcesRequest(),
				]);
				setExpenseCategories(expenseRes.categories || []);
				setIncomeCategories(sortIncomeCategories(incomeRes.categories || []));
				setIncomeSources(sourcesRes.sources || []);
			} catch (error) {
				console.error('Failed to load categories:', error);
			}
		};

		if (open) {
			fetchCategories();
		}
	}, [open]);

	useEffect(() => {
		if (open) {
			setActiveTab(defaultTab);
			if (editingExpense) {
				// Pre-fill form with expense data
				// Remove unnecessary decimals from amount
				const amountValue = editingExpense.amount;
				let formattedAmount = '';
				if (amountValue != null) {
					const numValue =
						typeof amountValue === 'string'
							? parseFloat(amountValue)
							: amountValue;
					const strValue = numValue.toString();
					// Only remove trailing zeros if there's a decimal point
					formattedAmount = strValue.includes('.')
						? strValue.replace(/\.?0+$/, '')
						: strValue;
				}
				setAmount(formattedAmount);
				setCurrency(editingExpense.currency || 'AMD');
				// For recurring items, use start_date; for regular, use date (parse as local date, no UTC)
				const dateValue = editingExpense.start_date || editingExpense.date;
				setDate(
					dateValue ? (parseDateOnly(dateValue) ?? new Date()) : new Date(),
				);
				const desc = editingExpense.description || '';
				setDescription(desc);
				// For recurring items, convert recurring_period to frequency format
				if (editingExpense.recurring_period) {
					const recurringPeriod = editingExpense.recurring_period;
					setFrequency(
						recurringPeriod === 'weekly'
							? intl.formatMessage({ id: 'AddRecordModalID25' })
							: recurringPeriod === 'monthly'
								? intl.formatMessage({ id: 'AddRecordModalID24' })
								: intl.formatMessage({ id: 'AddRecordModalID24' }),
					);
					setRemindMe(true);
				} else {
					setFrequency(
						editingExpense.frequency ||
							intl.formatMessage({ id: 'AddRecordModalID24' }),
					);
					setRemindMe(editingExpense.remindMe || false);
				}
				setContent(desc);
			} else if (editingIncome) {
				// Pre-fill form with income data
				// Remove unnecessary decimals from amount
				const amountValue = editingIncome.amount;
				let formattedAmount = '';
				if (amountValue != null) {
					const numValue =
						typeof amountValue === 'string'
							? parseFloat(amountValue)
							: amountValue;
					const strValue = numValue.toString();
					// Only remove trailing zeros if there's a decimal point
					formattedAmount = strValue.includes('.')
						? strValue.replace(/\.?0+$/, '')
						: strValue;
				}
				setAmount(formattedAmount);
				setCurrency(editingIncome.currency || 'AMD');
				// For recurring items, use start_date; for regular, use date (parse as local date, no UTC)
				const dateValue = editingIncome.start_date || editingIncome.date;
				setDate(
					dateValue ? (parseDateOnly(dateValue) ?? new Date()) : new Date(),
				);
				const desc = editingIncome.description || editingIncome.note || '';
				setDescription(desc);
				// For recurring items, convert recurring_period to frequency format
				if (editingIncome.recurring_period) {
					const recurringPeriod = editingIncome.recurring_period;
					setFrequency(
						recurringPeriod === 'weekly'
							? intl.formatMessage({ id: 'AddRecordModalID25' })
							: recurringPeriod === 'monthly'
								? intl.formatMessage({ id: 'AddRecordModalID24' })
								: intl.formatMessage({ id: 'AddRecordModalID24' }),
					);
					setRemindMe(true);
				} else {
					setFrequency(
						editingIncome.frequency ||
							intl.formatMessage({ id: 'AddRecordModalID24' }),
					);
					setRemindMe(editingIncome.remindMe || false);
				}
				setContent(desc);
			} else if (prefillFromRecurringNotification) {
				// Pre-fill from recurring notification (accept flow): one-off record, no recurring
				const prefill = prefillFromRecurringNotification;
				const amountValue = prefill.amount;
				let formattedAmount = '';
				if (amountValue != null) {
					const numValue =
						typeof amountValue === 'string'
							? parseFloat(amountValue)
							: amountValue;
					const strValue = numValue.toString();
					formattedAmount = strValue.includes('.')
						? strValue.replace(/\.?0+$/, '')
						: strValue;
				}
				setAmount(formattedAmount);
				setCurrency(prefill.currency || 'AMD');
				const dateValue = prefill.occurrence_date;
				setDate(
					dateValue ? (parseDateOnly(dateValue) ?? new Date()) : new Date(),
				);
				const desc = prefill.description || '';
				setDescription(desc);
				setContent(desc);
				setFrequency(intl.formatMessage({ id: 'AddRecordModalID24' }));
				setRemindMe(false);
			} else {
				// Reset form for new record - use currency from user profile
				const userCurrency = userInfo?.currency_preference;
				setCurrency(
					SUPPORTED_CURRENCY_CODES.includes(userCurrency)
						? userCurrency
						: 'AMD',
				);
				setAmount('');
				setCategory({ id: '', name: '' });
				setSource({ id: '', name: '' });
				setDate(
					(defaultDate instanceof Date
						? defaultDate
						: parseDateOnly(defaultDate)) ?? getLastUsedRecordDate(),
				);
				setDescription('');
				setFrequency(intl.formatMessage({ id: 'AddRecordModalID24' }));
				setRemindMe(defaultRecurring);
				setContent('');
				setDescriptionLength(0);
			}
			setShowError('');
			setFieldErrors({
				amount: false,
				category: false,
				source: false,
				date: false,
			});
			setBackendFieldErrors({
				amount: false,
				category: false,
				source: false,
				date: false,
			});
		}
	}, [
		open,
		defaultTab,
		editingExpense,
		editingIncome,
		prefillFromRecurringNotification,
		defaultRecurring,
		defaultDate,
		userInfo?.currency_preference,
	]);

	// Separate effect to set category when categories are loaded and we're editing or pre-filling from notification
	useEffect(() => {
		if (
			open &&
			editingExpense &&
			editingExpense.category_id &&
			expenseCategories.length > 0
		) {
			const expenseCategory = expenseCategories.find(
				cat => String(cat.id) === String(editingExpense.category_id),
			);
			if (expenseCategory && !category.id) {
				setCategory({
					id: expenseCategory.id,
					name: expenseCategory.name,
					original_name: expenseCategory.original_name || expenseCategory.name,
				});
			}
		}
		if (
			open &&
			editingIncome &&
			editingIncome.category_id &&
			incomeCategories.length > 0
		) {
			const incomeCategory = incomeCategories.find(
				cat => String(cat.id) === String(editingIncome.category_id),
			);
			if (incomeCategory && !category.id) {
				setCategory({
					id: incomeCategory.id,
					name: incomeCategory.name,
					original_name: incomeCategory.original_name || incomeCategory.name,
				});
			}
		}
		if (
			open &&
			prefillFromRecurringNotification &&
			prefillFromRecurringNotification.category_name
		) {
			const prefill = prefillFromRecurringNotification;
			const categories =
				prefill.recurring_type === 'expense'
					? expenseCategories
					: incomeCategories;
			if (categories.length > 0 && !category.id) {
				const match = categories.find(
					cat =>
						cat.name &&
						String(cat.name).toLowerCase() ===
							String(prefill.category_name).toLowerCase(),
				);
				if (match) {
					setCategory({
						id: match.id,
						name: match.name,
						original_name: match.original_name || match.name,
					});
				}
			}
		}
	}, [
		open,
		editingExpense,
		editingIncome,
		prefillFromRecurringNotification,
		expenseCategories,
		incomeCategories,
		category.id,
	]);

	// Separate effect to set source when sources are loaded and we're editing income
	useEffect(() => {
		if (
			open &&
			editingIncome &&
			editingIncome.source_id &&
			incomeSources.length > 0
		) {
			const incomeSource = incomeSources.find(
				src => String(src.id) === String(editingIncome.source_id),
			);
			if (incomeSource && !source.id) {
				setSource({
					id: incomeSource.id,
					name: incomeSource.name,
					original_name: incomeSource.original_name || incomeSource.name,
				});
			}
		}
	}, [open, editingIncome, incomeSources, source.id]);

	// Update contentEditable div when content changes (for editing mode)
	useEffect(() => {
		if (open && editableRef.current && content) {
			const htmlWithLinks = convertTextToHTML(content);
			if (editableRef.current.innerHTML !== htmlWithLinks) {
				editableRef.current.innerHTML = htmlWithLinks;
			}
		} else if (open && editableRef.current && !content) {
			editableRef.current.innerHTML = '';
		}
	}, [open, content]);

	// Ensure remindMe is always true when editing recurring items
	useEffect(() => {
		if (
			open &&
			((editingIncome && editingIncome.recurring_period) ||
				(editingExpense && editingExpense.recurring_period))
		) {
			setRemindMe(true);
		}
	}, [open, editingIncome, editingExpense]);

	const getCategoryIcon = categoryName => {
		const categoryItem = categoryData.find(item => item.name === categoryName);
		return categoryItem ? categoryItem.icon : null;
	};

	const getSourceIcon = sourceName => {
		if (!sourceName) return null;

		const sourceNameLower = sourceName.toLowerCase();
		const iconMap = {
			business: IncomeBusiness,
			բիզնես: IncomeBusiness,
			client: IncomeClient,
			հաճախորդ: IncomeClient,
			employer: IncomeEmployer,
			գործատու: IncomeEmployer,
			investment: IncomeInvestment,
			ներդրում: IncomeInvestment,
			other: IncomeOther,
			այլ: IncomeOther,
			rental: IncomeRental,
			վարձակալություն: IncomeRental,
		};
		// Try exact match first
		if (iconMap[sourceNameLower]) {
			return iconMap[sourceNameLower];
		}

		// Try partial match for flexibility
		for (const [key, icon] of Object.entries(iconMap)) {
			if (sourceNameLower.includes(key)) {
				return icon;
			}
		}

		return IncomeOther; // Default icon
	};

	// Function to parse backend error and identify which field has error
	const parseBackendError = errorMessage => {
		const newBackendErrors = {
			amount: false,
			category: false,
			source: false,
			date: false,
		};
		// Check for specific field errors in the error message
		if (errorMessage.toLowerCase().includes('amount')) {
			newBackendErrors.amount = true;
		}
		if (errorMessage.toLowerCase().includes('category')) {
			newBackendErrors.category = true;
		}
		if (errorMessage.toLowerCase().includes('source')) {
			newBackendErrors.source = true;
		}
		if (
			errorMessage.toLowerCase().includes('date') ||
			errorMessage.toLowerCase().includes('time')
		) {
			newBackendErrors.date = true;
		}

		return newBackendErrors;
	};

	const handleAddRecord = async () => {
		setShowError('');
		setDescriptionLength(0);
		setBackendFieldErrors({
			amount: false,
			category: false,
			source: false,
			date: false,
		});

		// Format date to string for API
		const formattedDate = format(date, 'yyyy-MM-dd');

		// Moving a recurring rule's date moves every future occurrence with it, so
		// start_date only travels when the user actually changed it - an untouched
		// date is left to the backend, which keeps the one it already stores.
		const movedRecurringStart = editing => {
			const current = String(editing?.start_date || '').split('T')[0];
			return current && current !== formattedDate
				? { start_date: formattedDate }
				: {};
		};

		// Validate amount - must be greater than 0
		const amountValue = parseFloat(amount);
		const isAmountInvalid = !amount || isNaN(amountValue) || amountValue <= 0;

		// Check if editing a recurring item (date not required for recurring updates)
		const isEditingRecurringItem =
			!isDuplicating &&
			((activeTab === 'income' &&
				editingIncome &&
				editingIncome.id &&
				editingIncome.recurring_period) ||
				(activeTab === 'expense' &&
					editingExpense &&
					editingExpense.id &&
					editingExpense.recurring_period));

		// Validate fields and set errors
		// Note: source_id is optional in backend, so we don't validate it
		const errors = {
			amount: isAmountInvalid,
			category: !category.id,
			date: !isEditingRecurringItem && !date, // Date not required when editing recurring items
			source: false, // source_id is optional, not required
		};

		setFieldErrors(errors);

		// Check if any errors exist
		const hasErrors = Object.values(errors).some(error => error);

		if (hasErrors) {
			// Check for amount validation first
			if (isAmountInvalid) {
				if (!amount) {
					setShowError(
						intl.formatMessage({ id: 'AddRecordModalErrorMassageID1' }),
					);
				} else if (isNaN(amountValue)) {
					setShowError(
						intl.formatMessage({ id: 'AddRecordModalErrorMassageID2' }),
					);
				} else if (amountValue <= 0) {
					setShowError(
						intl.formatMessage({ id: 'AddRecordModalErrorMassageID3' }),
					);
				}
				return;
			}

			// Other field errors
			let errorMessage = intl.formatMessage({
				id: 'AddRecordModalErrorMassageID4',
			});
			const missingFields = [];

			if (errors.category)
				missingFields.push(
					intl.formatMessage({ id: 'AddRecordModalErrorMassageID10' }),
				);
			if (errors.date)
				missingFields.push(
					intl.formatMessage({ id: 'AddRecordModalErrorMassageID11' }),
				);
			// Source is optional, so we don't add it to missing fields

			errorMessage += missingFields.join(', ');
			setShowError(errorMessage);
			return;
		}

		setLoading(true);
		const payment_method = 'cash';

		try {
			// Always create regular item first
			const recordData = {
				amount: parseFloat(amount),
				currency,
				category_id: category.id,
				date: formattedDate,
				description,
				frequency,
				remindMe,
				payment_method,
				...(activeTab === 'income' && source.id && { source_id: source.id }),
			};

			// Custom save path (e.g. statement import draft edit) — same form, different API
			if (typeof onCustomSave === 'function') {
				await onCustomSave({
					...recordData,
					type: activeTab,
				});
				setAmount('');
				setCategory({ id: '', name: '' });
				setSource({ id: '', name: '' });
				setDate(getLastUsedRecordDate());
				setDescription('');
				setFrequency(intl.formatMessage({ id: 'AddRecordModalID24' }));
				setRemindMe(false);
				setShowError('');
				setDescriptionLength(0);
				setFieldErrors({
					amount: false,
					category: false,
					source: false,
					date: false,
				});
				setBackendFieldErrors({
					amount: false,
					category: false,
					source: false,
					date: false,
				});
				setLoading(false);
				onClose();
				if (onSuccess) onSuccess();
				return;
			}

			// Determine if this should be a recurring item
			const isRecurring =
				remindMe &&
				(frequency === intl.formatMessage({ id: 'AddRecordModalID25' }) ||
					frequency === intl.formatMessage({ id: 'AddRecordModalID24' }));

			let response;

			// Check if editing a recurring item
			const isEditingRecurringIncome =
				!isDuplicating &&
				activeTab === 'income' &&
				editingIncome &&
				editingIncome.id &&
				editingIncome.recurring_period;
			const isEditingRecurringExpense =
				!isDuplicating &&
				activeTab === 'expense' &&
				editingExpense &&
				editingExpense.id &&
				editingExpense.recurring_period;
			const isDuplicatingRecurring =
				isDuplicating &&
				((activeTab === 'income' && editingIncome?.recurring_period) ||
					(activeTab === 'expense' && editingExpense?.recurring_period));

			// Create or update regular/recurring income/expense
			if (activeTab === 'income') {
				if (isEditingRecurringIncome) {
					// Update existing recurring income
					const recurringData = {
						amount: parseFloat(amount),
						currency,
						category_id: category.id,
						description,
						recurring_period: frequencyToRecurringPeriod(frequency),
						...movedRecurringStart(editingIncome),
						...(source.id && { source_id: source.id }),
					};
					response = await recurringIncomesUpdateRequest(
						editingIncome.id,
						recurringData,
					);
				} else if (isDuplicatingRecurring) {
					const recurringData = {
						amount: parseFloat(amount),
						currency,
						category_id: category.id,
						date: formattedDate,
						description,
						recurring_period: frequencyToRecurringPeriod(frequency),
						start_date: formattedDate,
						end_date: null,
						...(source.id && { source_id: source.id }),
					};
					response = await recurringIncomesCreateRequest(recurringData);
				} else if (!isDuplicating && editingIncome && editingIncome.id) {
					// Update existing regular income
					response = await incomesUpdateRequest(editingIncome.id, recordData);
				} else {
					// Create new income
					response = await incomesCreateRequest(recordData);
				}
			} else if (activeTab === 'expense') {
				if (isEditingRecurringExpense) {
					// Update existing recurring expense
					const recurringData = {
						amount: parseFloat(amount),
						currency,
						category_id: category.id,
						description,
						recurring_period: frequencyToRecurringPeriod(frequency),
						...movedRecurringStart(editingExpense),
						payment_method: 'cash',
					};
					response = await recurringExpensesUpdateRequest(
						editingExpense.id,
						recurringData,
					);
				} else if (isDuplicatingRecurring) {
					const recurringData = {
						amount: parseFloat(amount),
						currency,
						category_id: category.id,
						date: formattedDate,
						description,
						recurring_period: frequencyToRecurringPeriod(frequency),
						start_date: formattedDate,
						end_date: null,
						payment_method: 'cash',
					};
					response = await recurringExpensesCreateRequest(recurringData);
				} else if (!isDuplicating && editingExpense && editingExpense.id) {
					// Update existing regular expense
					response = await expensesUpdateRequest(editingExpense.id, recordData);
				} else {
					// Create new expense
					response = await expensesCreateRequest(recordData);
				}
			} else if (activeTab === 'goal') {
			}

			// Create recurring record for new items or when converting a regular item to recurring
			if (
				isRecurring &&
				!isEditingRecurringIncome &&
				!isEditingRecurringExpense &&
				!isDuplicatingRecurring
			) {
				const recurringData = {
					amount: parseFloat(amount),
					currency,
					category_id: category.id,
					date: formattedDate,
					description,
					recurring_period: frequencyToRecurringPeriod(frequency),
					start_date: formattedDate,
					end_date: null, // Optional, can be null
					...(activeTab === 'income' && source.id && { source_id: source.id }),
					...(activeTab === 'expense' && { payment_method: 'cash' }),
				};

				// Call recurring endpoint
				if (activeTab === 'income') {
					const recurringResponse =
						await recurringIncomesCreateRequest(recurringData);
					// Check for errors in recurring response
					if (
						recurringResponse &&
						recurringResponse.response &&
						recurringResponse.response.data &&
						recurringResponse.response.data.detail
					) {
						let errorMsg;
						// Handle both string detail (HTTPException) and array detail (Pydantic validation)
						if (typeof recurringResponse.response.data.detail === 'string') {
							errorMsg = recurringResponse.response.data.detail;
						} else if (
							Array.isArray(recurringResponse.response.data.detail) &&
							recurringResponse.response.data.detail.length > 0
						) {
							errorMsg = recurringResponse.response.data.detail[0].msg;
							errorMsg = errorMsg.split(' ').slice(2).join(' ') || errorMsg;
						} else {
							errorMsg = intl.formatMessage({
								id: 'AddRecordModalErrorMassageID5',
							});
						}
						setShowError(
							`${intl.formatMessage({ id: 'AddRecordModalErrorMassageID6' })} ${errorMsg}`,
						);
						setLoading(false);
						return;
					}
				} else if (activeTab === 'expense') {
					const recurringResponse =
						await recurringExpensesCreateRequest(recurringData);
					// Check for errors in recurring response
					if (
						recurringResponse &&
						recurringResponse.response &&
						recurringResponse.response.data &&
						recurringResponse.response.data.detail
					) {
						let errorMsg;
						// Handle both string detail (HTTPException) and array detail (Pydantic validation)
						if (typeof recurringResponse.response.data.detail === 'string') {
							errorMsg = recurringResponse.response.data.detail;
						} else if (
							Array.isArray(recurringResponse.response.data.detail) &&
							recurringResponse.response.data.detail.length > 0
						) {
							errorMsg = recurringResponse.response.data.detail[0].msg;
							errorMsg = errorMsg.split(' ').slice(2).join(' ') || errorMsg;
						} else {
							errorMsg = intl.formatMessage({
								id: 'AddRecordModalErrorMassageID5',
							});
						}
						setShowError(
							`${intl.formatMessage({ id: 'AddRecordModalErrorMassageID6' })} ${errorMsg}`,
						);
						setLoading(false);
						return;
					}
				}
			}

			// Handle backend validation errors
			if (
				response &&
				response.response &&
				response.response.data &&
				response.response.data.detail
			) {
				let errorMsg;
				// Handle both string detail (HTTPException) and array detail (Pydantic validation)
				if (typeof response.response.data.detail === 'string') {
					errorMsg = response.response.data.detail;
				} else if (
					Array.isArray(response.response.data.detail) &&
					response.response.data.detail.length > 0
				) {
					errorMsg = response.response.data.detail[0].msg;
					errorMsg = errorMsg.split(' ').slice(2).join(' ') || errorMsg;
				} else {
					errorMsg = intl.formatMessage({
						id: 'AddRecordModalErrorMassageID5',
					});
				}

				// Parse the error to identify which field is problematic
				const backendErrors = parseBackendError(errorMsg);
				setBackendFieldErrors(backendErrors);

				setShowError(errorMsg);
				setLoading(false);
				return;
			}

			// Easter egg: expense 69 → raining carrots (remove with CarrotRainEasterEgg)
			if (
				activeTab === 'expense' &&
				!editingExpense &&
				parseFloat(amount) === 69
			) {
				window.dispatchEvent(new CustomEvent('carrot-rain'));
			}

			// If opened from recurring notification accept flow, mark notification as accepted (skip_create)
			if (prefillFromRecurringNotification?.id) {
				try {
					await acceptRecurringNotificationSkipCreate(
						prefillFromRecurringNotification.id,
						userLanguage,
					);
				} catch (err) {
					console.error('Failed to mark notification as accepted:', err);
					setShowError(
						err.message ||
							intl.formatMessage({
								id: 'AddRecordModalErrorMassageID7',
							}),
					);
					setLoading(false);
					return;
				}
			}

			const isEditingExisting =
				!isDuplicating &&
				((activeTab === 'income' && Boolean(editingIncome?.id)) ||
					(activeTab === 'expense' && Boolean(editingExpense?.id)));
			if (!isEditingExisting) {
				saveLastUsedRecordDate(date);
			}

			// Reset everything on success so form is clean for next use
			setAmount('');
			setCategory({ id: '', name: '' });
			setSource({ id: '', name: '' });
			setDate(getLastUsedRecordDate());
			setDescription('');
			setFrequency(intl.formatMessage({ id: 'AddRecordModalID24' }));
			setRemindMe(false);
			setShowError('');
			setDescriptionLength(0);
			setFieldErrors({
				amount: false,
				category: false,
				source: false,
				date: false,
			});
			setBackendFieldErrors({
				amount: false,
				category: false,
				source: false,
				date: false,
			});

			onClose();
			if (onSuccess) onSuccess();
		} catch (error) {
			console.error('Error adding record:', error);

			let errorMessage = intl.formatMessage({
				id: 'AddRecordModalErrorMassageID8',
			});

			if (error.response && error.response.data) {
				// Handle both string detail (HTTPException) and array detail (Pydantic validation)
				if (typeof error.response.data.detail === 'string') {
					errorMessage = error.response.data.detail;
				} else if (
					Array.isArray(error.response.data.detail) &&
					error.response.data.detail.length > 0
				) {
					errorMessage = error.response.data.detail[0].msg;
					errorMessage =
						errorMessage.split(' ').slice(2).join(' ') || errorMessage;
				} else {
					errorMessage = error.response.data.message || errorMessage;
				}

				// Try to parse backend error for field identification
				const backendErrors = parseBackendError(errorMessage);
				setBackendFieldErrors(backendErrors);
			} else if (error.request) {
				errorMessage = intl.formatMessage({
					id: 'AddRecordModalErrorMassageID9',
				});
			}

			setShowError(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	// Combined error check - shows error if either frontend or backend validation failed
	const hasFieldError = fieldName => {
		return fieldErrors[fieldName] || backendFieldErrors[fieldName];
	};

	const handleTabChange = tab => {
		setActiveTab(tab);
		setCategory({ id: '', name: '' });
		setSource({ id: '', name: '' });
		setShowError('');
		setDescriptionLength(0);
		setFieldErrors({
			amount: false,
			category: false,
			source: false,
			date: false,
		});
		setBackendFieldErrors({
			amount: false,
			category: false,
			source: false,
			date: false,
		});
	};

	const handleCategorySelect = cat => {
		setCategory({
			id: cat.id,
			name: cat.name,
			original_name: cat.original_name || cat.name,
		});
		setShowCategoryDropdown(false);
	};

	const handleSourceSelect = src => {
		setSource({
			id: src.id,
			name: src.name,
			original_name: src.original_name || src.name,
		});
		setShowSourceDropdown(false);
	};

	const handleCloseError = () => {
		setShowError('');
		// Also clear backend field errors when user closes error
		setBackendFieldErrors({
			amount: false,
			category: false,
			source: false,
			date: false,
		});
		setDescriptionLength(0);
	};

	if (!open) return null;

	return (
		<div className={styles.modalOverlay} onClick={onClose}>
			<div className={styles.modalContent} onClick={e => e.stopPropagation()}>
				<button className={styles.closeButton} onClick={onClose}>
					<X size={20} />
				</button>

				{showError && (
					<div className={styles.errorMessageContainer}>
						<div className={styles.errorMessage}>
							<span>{showError}</span>
							<button
								onClick={handleCloseError}
								className={styles.errorCloseButton}
							>
								<X size={14} />
							</button>
						</div>
					</div>
				)}

				{/* Title - Show when editing */}
				{editingExpense || editingIncome ? (
					<h2 className={styles.modalTitle}>
						{editingIncome
							? editingIncome.recurring_period
								? intl.formatMessage({ id: 'AddRecordModalID1' })
								: intl.formatMessage({ id: 'AddRecordModalID2' })
							: editingExpense.recurring_period
								? intl.formatMessage({ id: 'AddRecordModalID3' })
								: intl.formatMessage({ id: 'AddRecordModalID4' })}
					</h2>
				) : (
					<h1 className={styles.tabsTitle}>
						{intl.formatMessage({ id: 'AddRecordModalID26' })}
					</h1>
				)}

				{/* Tabs - Only show when not editing and not pre-filling from recurring notification */}

				{!editingExpense &&
					!editingIncome &&
					!prefillFromRecurringNotification && (
						<div className={styles.tabs}>
							<button
								className={`${styles.tab} ${
									activeTab === 'income' ? styles.tabActive : ''
								}`}
								onClick={() => handleTabChange('income')}
							>
								{intl.formatMessage({ id: 'AddRecordModalID5' })}
							</button>
							<button
								className={`${styles.tab} ${
									activeTab === 'expense' ? styles.tabActive : ''
								}`}
								onClick={() => handleTabChange('expense')}
							>
								{intl.formatMessage({ id: 'AddRecordModalID6' })}
							</button>
						</div>
					)}

				<div className={styles.amountRow}>
					<div className={styles.formGroup}>
						<label
							className={`${styles.label} ${
								hasFieldError('amount') ? styles.labelError : ''
							}`}
						>
							{intl.formatMessage({ id: 'AddRecordModalID7' })}
						</label>
						<input
							type='number'
							placeholder={intl.formatMessage({ id: 'AddRecordModalID8' })}
							value={amount}
							onChange={e => {
								const value = e.target.value;
								// Allow empty string for clearing, but prevent negative values
								if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
									setAmount(value);
								}
							}}
							min='0.01'
							step='any'
							className={`${styles.input} ${
								hasFieldError('amount') ? styles.inputError : ''
							}`}
							required
						/>
					</div>
					<div className={styles.currencyGroup}>
						<label className={styles.label}>
							{intl.formatMessage({ id: 'AddRecordModalID9' })}
						</label>
						<div className={styles.dropdownContainer} ref={currencyRef}>
							<div
								className={`${styles.currencyBox} ${
									hasFieldError('amount') ? styles.inputError : ''
								}`}
								onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
							>
								{currency}
								<ChevronDown size={16} className={styles.chevronIcon} />
							</div>
							{showCurrencyDropdown && (
								<div className={styles.dropdownMenu}>
									{SUPPORTED_CURRENCY_CODES.map(curr => (
										<div
											key={curr}
											className={styles.dropdownItem}
											onClick={() => {
												setCurrency(curr);
												setShowCurrencyDropdown(false);
											}}
										>
											{curr}
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Source Dropdown - Only for Income */}
				{activeTab === 'income' && (
					<div className={styles.formGroup}>
						<label
							className={`${styles.label} ${
								hasFieldError('source') ? styles.labelError : ''
							}`}
						>
							{intl.formatMessage({ id: 'AddRecordModalID23' })}
						</label>
						<div className={styles.dropdownContainer} ref={sourceRef}>
							<div
								className={`${styles.selectField} ${
									hasFieldError('source') ? styles.selectFieldError : ''
								}`}
								onClick={() => setShowSourceDropdown(!showSourceDropdown)}
							>
								{source.name ? (
									<>
										{getSourceIcon(source.original_name || source.name) ? (
											<img
												src={getSourceIcon(source.original_name || source.name)}
												alt={source.name}
												className={styles.fieldIcon}
											/>
										) : (
											<QrCode className={styles.fieldIcon} />
										)}
										<span className={styles.selectedValue}>{source.name}</span>
									</>
								) : (
									<>
										<QrCode className={styles.fieldIcon} />
										<span className={styles.placeholder}>
											{intl.formatMessage({ id: 'AddRecordModalID10' })}
										</span>
									</>
								)}
								<ChevronDown size={18} className={styles.chevronIcon} />
							</div>
							{showSourceDropdown && (
								<div className={styles.dropdownMenu}>
									{incomeSources.map(src => {
										const sourceIcon = getSourceIcon(
											src.original_name || src.name,
										);
										return (
											<div
												key={src.id}
												className={styles.dropdownItem}
												onClick={() => handleSourceSelect(src)}
											>
												{sourceIcon ? (
													<img
														src={sourceIcon}
														alt={src.name}
														className={styles.categoryIcon}
													/>
												) : (
													<QrCode className={styles.fieldIcon} />
												)}
												<span>{src.name}</span>
											</div>
										);
									})}
								</div>
							)}
						</div>
					</div>
				)}

				{/* Category Dropdown */}
				<div className={styles.formGroup}>
					<label
						className={`${styles.label} ${
							hasFieldError('category') ? styles.labelError : ''
						}`}
					>
						{intl.formatMessage({ id: 'AddRecordModalID12' })}
					</label>
					<div className={styles.dropdownContainer} ref={categoryRef}>
						<div
							className={`${styles.selectField} ${
								hasFieldError('category') ? styles.selectFieldError : ''
							}`}
							onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
						>
							{category.name ? (
								<>
									{getCategoryIcon(category.original_name || category.name) ? (
										<img
											src={getCategoryIcon(
												category.original_name || category.name,
											)}
											alt={category.name}
											className={styles.fieldIcon}
										/>
									) : (
										<QrCode className={styles.fieldIcon} />
									)}
									<span className={styles.selectedValue}>{category.name}</span>
								</>
							) : (
								<>
									<QrCode className={styles.fieldIcon} />
									<span className={styles.placeholder}>
										{intl.formatMessage({ id: 'AddRecordModalID13' })}
										{intl.formatMessage({ id: 'AddRecordModalID14' })}
									</span>
								</>
							)}
							<ChevronDown size={18} className={styles.chevronIcon} />
						</div>
						{showCategoryDropdown && (
							<div className={styles.dropdownMenu}>
								{currentCategories.map(cat => {
									const categoryIcon = getCategoryIcon(
										cat.original_name || cat.name,
									);
									return (
										<div
											key={cat.id}
											className={styles.dropdownItem}
											onClick={() => handleCategorySelect(cat)}
										>
											{categoryIcon ? (
												<img
													src={categoryIcon}
													alt={cat.name}
													className={styles.categoryIcon}
												/>
											) : (
												<QrCode className={styles.fieldIcon} />
											)}
											<span>{cat.name}</span>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>

				{/* Date Field with DatePicker - Hidden when editing recurring items */}
				{!(
					!isDuplicating &&
					((activeTab === 'income' &&
						editingIncome &&
						editingIncome.id &&
						editingIncome.recurring_period) ||
						(activeTab === 'expense' &&
							editingExpense &&
							editingExpense.id &&
							editingExpense.recurring_period))
				) && (
					<div className={styles.formGroup}>
						<label
							className={`${styles.label} ${
								hasFieldError('date') ? styles.labelError : ''
							}`}
						>
							{intl.formatMessage({ id: 'AddRecordModalID15' })}
						</label>
						<div className={styles.dropdownContainer}>
							<DatePicker
								selected={date}
								onChange={selectedDate => {
									const nextDate = selectedDate || new Date();
									setDate(nextDate);
									const isEditingExisting =
										!isDuplicating &&
										((activeTab === 'income' && Boolean(editingIncome?.id)) ||
											(activeTab === 'expense' && Boolean(editingExpense?.id)));
									if (selectedDate && !isEditingExisting) {
										saveLastUsedRecordDate(nextDate);
									}
								}}
								dateFormat='yyyy-MM-dd'
								customInput={<CustomDateInput error={hasFieldError('date')} />}
								popperClassName={styles.datePickerPopper}
								wrapperClassName={styles.datePickerWrapper}
								calendarClassName={styles.datePickerCalendar}
								showPopperArrow={false}
								popperPlacement='bottom-start'
								todayButton='Today'
								showMonthDropdown
								showYearDropdown
								dropdownMode='scroll'
								scrollableYearDropdown
								yearDropdownItemNumber={15}
								isClearable
								onClear={() => setDate(new Date())}
								clearButtonTitle='Clear'
							/>
						</div>
					</div>
				)}

				{/* <div className={styles.formGroup}>
          <label className={styles.label}>
            {intl.formatMessage({ id: "AddRecordModalID16" })}
          </label>

          <div className={styles.textareaWrapper}>
            {(!description || description.trim() === "") &&
              (!content || content.trim() === "") && (
                <LinkIcon size={18} className={styles.textareaIcon} />
              )}
            <div
              ref={editableRef}
              contentEditable
              onInput={handleInput}
              onPaste={handlePaste}
              onFocus={handleFocus}
              className={styles.textarea}
              style={{
                minHeight: "72px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                paddingLeft: description || content ? "14px" : "40px",
                paddingTop: "10px",
              }}
            />
          </div>
        </div> */}

				<div className={styles.formGroup}>
					<label className={styles.label}>
						{intl.formatMessage({ id: 'AddRecordModalID16' })}
					</label>

					<div className={styles.textareaWrapper}>
						{(!description || description.trim() === '') &&
							(!content || content.trim() === '') && (
								<LinkIcon size={18} className={styles.textareaIcon} />
							)}
						<div
							ref={editableRef}
							contentEditable
							onInput={handleInput}
							onPaste={handlePaste}
							onFocus={handleFocus}
							className={styles.textarea}
							style={{
								minHeight: '72px',
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-word',
								paddingLeft: description || content ? '14px' : '40px',
								paddingTop: '10px',
								border:
									descriptionLength > MAX_DESCRIPTION_LENGTH
										? '1.5px solid #ef4444'
										: undefined,
							}}
						/>
					</div>

					{descriptionLength > 0 && (
						<div
							style={{
								textAlign: 'right',
								fontSize: '12px',
								marginTop: '4px',
								color:
									descriptionLength > MAX_DESCRIPTION_LENGTH
										? '#ef4444'
										: '#9ca3af',
							}}
						>
							{descriptionLength}/{MAX_DESCRIPTION_LENGTH}
						</div>
					)}
				</div>

				{!prefillFromRecurringNotification && !onCustomSave && (
					<div className={styles.remindRow}>
						<Bell size={18} className={styles.bellIcon} />
						<input
							type='checkbox'
							id='recurring-checkbox'
							checked={remindMe}
							onChange={e => setRemindMe(e.target.checked)}
							className={styles.checkbox}
							disabled={
								(editingIncome && editingIncome.recurring_period) ||
								(editingExpense && editingExpense.recurring_period)
							}
						/>
						<label
							htmlFor='recurring-checkbox'
							className={styles.remindLabel}
							style={{
								cursor:
									(editingIncome && editingIncome.recurring_period) ||
									(editingExpense && editingExpense.recurring_period)
										? 'default'
										: 'pointer',
							}}
						>
							{intl.formatMessage({ id: 'AddRecordModalID17' })}
						</label>

						{remindMe && !prefillFromRecurringNotification && (
							<div className={styles.dropdownContainer} ref={frequencyRef}>
								<div
									className={styles.frequencyRow}
									onClick={() =>
										setShowFrequencyDropdown(!showFrequencyDropdown)
									}
								>
									<span className={styles.frequencyText}>{frequency}</span>
									<ChevronDown size={18} />
								</div>
								{showFrequencyDropdown && (
									<div className={styles.dropdownMenu}>
										{frequencies.map(freq => (
											<div
												key={freq}
												className={styles.dropdownItem}
												onClick={() => {
													setFrequency(freq);
													setShowFrequencyDropdown(false);
												}}
											>
												{freq}
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				)}

				<div className={styles.actions}>
					<button
						onClick={handleAddRecord}
						className={styles.addButton}
						disabled={loading}
					>
						{loading
							? intl.formatMessage({ id: 'AddRecordModalID20' })
							: intl.formatMessage({ id: 'AddRecordModalID21' })}
					</button>
					<button
						onClick={onClose}
						className={styles.cancelButton}
						disabled={loading}
					>
						{intl.formatMessage({ id: 'AddRecordModalID22' })}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AddRecordModal;
